/**
 * What a cue from beat 5 *means*, in a scene made of triangles.
 *
 * `src/story/act1/cullCorridor.ts` says "the machine takes a cycle". This file
 * is the only thing that knows a machine is a sound and a sheet of plastic, that
 * a bin has castors, and that being carried is a camera inside a box. Same seam
 * every beat since beat 1 has kept, and the same consequence: the beat replays
 * in Node with no renderer.
 *
 * ## The two clocks, and why only one of them is here
 *
 * The plant cycle is **4.2 s and it is the room's own clock**. It runs in the
 * fixed step so that a replay lands the same thumps in the same places, and the
 * loop that plays it is started by this file at the same instant the timer is
 * zeroed. It was nearly `cullCorridorAmbience()`'s job, and that would have been
 * wrong by however long the boot took: a bed started at scene assembly and a
 * timer started at the beat's first stage drift, and the drift is exactly the
 * thing the sheeting's lift is synchronised to.
 *
 * ## Nothing in here is watching her
 *
 * There is one human in this beat and he arrives in the last thirty seconds to
 * collect a bin. **He has no vision cone**, this room registers no watchers, and
 * `StealthSystem` is not constructed for it at all. That is not an omission to
 * be tidied up later — see `docs/beat-5-plan.md` rule 1. A lenient cone here
 * would turn the beat's payoff into a stealth check and undo the whole room.
 */

import * as THREE from 'three';
import { CULL_CUES } from '@/story/act1/cullCorridor';
import { plasticSheeting } from '@/models/env/plastic-sheeting';
import { findWorkerParts, workerAsset, type WorkerParts } from '@/models/env/worker';
import { getAsset } from '@/models/registry';
import { disposeAsset } from '@/models/materials';
import { instanceGroup } from '@/world/instancing';
import { FadeOverlay } from '@/world/fadeOverlay';
import type { Cue } from '@/story/types';
import {
  CULL,
  CULL_BIN,
  CULL_BIN_AT,
  CULL_DOOR_AT,
  CULL_THRESHOLD,
  SHEETING_POSE,
  type CullCorridorParts,
} from '@/world/cullCorridor';

/**
 * The plant's cycle, in seconds, and the three moments inside it.
 *
 * One period, never varying, never reacting. `hiss` is when the air is released
 * and therefore when the sheeting moves; `settle` is when it has fallen back.
 * **Nothing in the beat is gated on any of these numbers** — they are a rhythm,
 * not a window, and the moment one of them becomes a condition for something the
 * player must do, this beat has become the puzzle it exists not to be.
 */
const PLANT = { period: 4.2, hiss: 0.35, settle: 1.55 } as const;

/** How far the sheeting lifts: ordinarily, and while she is beside the mouth. */
const LIFT = { far: 0.42, near: 1.0 } as const;

/** The man: how fast he walks in, how long he takes over it, how fast he pushes. */
const COLLECTOR = {
  walkSpeed: 1.05,
  pushSpeed: 0.95,
  /** One stride, metres. The gait and the footfall come off the same number. */
  stride: 0.68,
  /** Where he waits before the beat needs him: beyond the door, off-stage. */
  parkZ: CULL_THRESHOLD.maxZ - 0.8,
  /**
   * How far upstream of the bin he stops, metres — the side he pushes FROM.
   *
   * **He used to stop at `binZ + 0.75` and then teleport to `binZ − 0.75`** on
   * the frame the push began, because `arriving` walked to one side of the bin
   * and `pushing` pinned him to the other. A metre and a half, in one frame,
   * with a man's legs mid-stride. Nobody had ever seen it: the camera was inside
   * the bin for the whole of it. The external framing this beat now uses points
   * straight at him, so he walks *past* the bin and takes hold of it from behind.
   */
  standOff: 0.85,
  /** How long he spends righting the bin and taking hold of it. */
  dwell: 2.2,
} as const;

/**
 * How long the bin takes to go over, and to come back up, in seconds.
 *
 * The fall is quick and the lift is slow, which is the difference between a
 * thing losing its balance and a man deciding to pick it up. Neither is eased at
 * the ends by an S-curve: a bin that decelerates into the floor reads as a prop
 * being placed, and the whole point of the topple is that it is not staged.
 */
const TIP = { fallSeconds: 0.55, rightSeconds: 1.5 } as const;

/**
 * Where the camera sits once she is in the bin, and it used to be INSIDE it.
 *
 * ── What was wrong, and it was not a matter of taste ────────────────────────
 *
 * The old shot put the lens at `(binX, 0.34, binZ)` looking up past the rim.
 * Her carry pose is `(binX, 0.22, binZ)` — the same x, the same z, 0.12 m below
 * the lens — and she is 0.32 m tall. **The camera was inside her body.** Every
 * frame of the beat's payoff was a wall of out-of-focus pig with a grey box
 * behind it, and `cull-corridor.spec.ts` asserted `lens.y < 0.6`, which is true
 * of a camera inside a pig, so the gate agreed.
 *
 * Alex played it and reported it as "hardly recognizable" and asked for the
 * outside: *"the camera shows external view and one can see how the trash bin is
 * moved by a person."* That is the better shot regardless of the bug, and it is
 * the one thing in the beat the plan had backwards. The horror of this room is
 * not what it looks like from inside a box. It is a man wheeling a bin down a
 * corridor at an ordinary walking pace, with a pig in it, **not looking down** —
 * and you cannot photograph a man not looking down from inside the bin.
 *
 * So: across the corridor and trailing, at bin height. The corridor is 3.2 m
 * wide and the bin runs down the west side at x = −1.0, so x = +1.15 is against
 * the east wall with the whole width between lens and subject, and the threshold
 * passage beyond the door is `halfX` 1.4, so the shot survives going through it.
 *
 * **`trail` was 1.6 and the first frames were a man's coat.** He pushes from
 * `binZ − COLLECTOR.standOff`, so the trail is measured against *him* and not
 * against the bin: at 1.6 he stood 0.75 m in front of the lens and filled the
 * frame edge to edge. At 3.9 he is a little over three metres out and the bin is
 * just past him — a man, the thing he is pushing, and the corridor behind them
 * both, which is the shot. Judge it against
 * `docs/verification/m1/cull-corridor/6-carried-past-the-bay.png`.
 */
const RIDE_CAMERA = {
  x: 1.15,
  y: 1.05,
  trail: 3.9,
  lookX: CULL_BIN_AT.x,
  lookY: 0.62,
  fov: 50,
} as const;

/** Where her body goes while she is being carried. Interior floor of the bin. */
const BIN_FLOOR_Y = 0.22;

export interface CullDirectorOptions {
  /** One plant cycle has thumped, at the bay. */
  onPlantCycle: (at: { x: number; y: number; z: number }) => void;
  /** The sheeting has moved. */
  onSheeting: (at: { x: number; y: number; z: number }) => void;
  /** A boot has landed. */
  onFootfall: (at: { x: number; y: number; z: number }) => void;
  /** The lid, once, and it is the last thing she hears from outside. */
  onLid: (at: { x: number; y: number; z: number }) => void;
  /**
   * The bin goes over. One impact, on the frame the shove takes.
   *
   * Reusing `onLid`'s sound rather than adding a registered one: the beat's
   * declared cut line this session is that nothing new goes through the audio
   * gate, and a wheeled bin hitting a concrete floor and a wheeled bin's lid
   * dropping are the same short hard plastic knock at different gains. If a
   * stopwatch says the topple wants its own weight, that is a new sound and a
   * `check:audio` run, not a louder lid.
   */
  onBinFall: (at: { x: number; y: number; z: number }) => void;
  /** The castors, while the bin is moving. Speed is 0 when it is not. */
  onCastors: (speed: number, at: { x: number; y: number; z: number }) => void;
}

/** An authored camera framing, the same shape `BornDirector` hands back. */
export interface Framing {
  position: { x: number; y: number; z: number };
  lookAt: { x: number; y: number; z: number };
  fov: number;
}

type Ride = 'idle' | 'arriving' | 'righting' | 'pushing' | 'gone';

/** `window.__rosalie.cullDebug`: the director's own view plus its fade. */
export type CullDebug = CullDirector['debug'] & { readonly fade: number };

/**
 * Where she stands inside the bin, lying and upright, as an offset from `binZ`.
 *
 * Two authored poses and a lerp between them, rather than one pose transformed
 * by the pivot's rotation, and the difference is not cosmetic: while the bin is
 * on its side she is standing on what is normally its **wall**, and when it is
 * upright she is standing on its **base**. Rotating one point through the topple
 * angle gets the upright case right and leaves her floating 0.36 m up inside the
 * lying one, because 0.22 m "up from the base" becomes 0.22 m *along* the bin the
 * moment it goes over.
 */
const BIN_POSE = {
  lying: { y: 0.02, dz: 0.62 },
  upright: { y: BIN_FLOOR_Y, dz: 0 },
} as const;

export class CullDirector {
  readonly #parts: CullCorridorParts;
  readonly #options: CullDirectorOptions;
  readonly #fadeOverlay: FadeOverlay;
  readonly #collector: THREE.Object3D;
  readonly #collectorParts: WorkerParts;
  readonly #doorLeaf: THREE.Object3D;
  readonly #doorHinge: THREE.Object3D;

  /** Seconds into the current plant cycle. Fixed step; nothing else may write it. */
  #cycle = 0;
  #running = false;
  /** 0..1, how hard the sheeting is being lifted at all. Set by the stage. */
  #liftScale: number = LIFT.far;
  /** The last lift the panels were actually rebuilt at. See `#setLift`. */
  #builtLift = -1;

  #ride: Ride = 'idle';
  #rideT = 0;
  #binZ = CULL_BIN_AT.z;
  #binSpeed = 0;
  #collectorZ = COLLECTOR.parkZ;
  #gaitDistance = 0;
  /**
   * How far over the bin is, 0 upright and 1 flat, and it is the beat's own
   * state rather than a render value.
   *
   * Fixed step, because `cullBinInterior()` is tested against her position in
   * `fixedUpdate` — whether she is inside the bin is a simulation fact and D-045
   * forbids deciding it from something written at display rate.
   */
  #tip = 0;
  /** Which way `#tip` is being driven, or 0 when it is where it belongs. */
  #tipDirection: -1 | 0 | 1 = 0;
  /** True once she is in the bin on her own feet, before anybody has lifted it. */
  #inside = false;
  #lidPlayed = false;
  #doorAngle = 0;
  #doorTarget = 0;
  #ended = false;

  constructor(scene: THREE.Object3D, parts: CullCorridorParts, options: CullDirectorOptions) {
    this.#parts = parts;
    this.#options = options;
    this.#fadeOverlay = new FadeOverlay();

    // The man is built here rather than handed in, unlike beat 2's worker, and
    // the difference is that he is not in the room until the beat wants him:
    // there is no dressed figure to swap out because a cull corridor with a man
    // standing in it is a different room. He is parked beyond the door.
    this.#collector = workerAsset.create();
    this.#collector.name = 'cull-collector';
    this.#collector.position.set(0, 0, COLLECTOR.parkZ);
    this.#collector.rotation.y = Math.PI;
    this.#collector.visible = false;
    this.#collector.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (mesh.isMesh) mesh.castShadow = true;
    });
    scene.add(this.#collector);
    this.#collectorParts = findWorkerParts(this.#collector);

    // A second copy of a door the dressing already places, for the reason
    // `corridorDirector.ts` gives: `instanceGroup` merges its placements into
    // world-space geometry, so the dressed door has no hinge left to turn.
    const doorAsset = getAsset('personnel-door');
    if (!doorAsset) throw new Error('cull director: the personnel-door asset is not registered');
    this.#doorLeaf = doorAsset.create();
    this.#doorLeaf.position.set(CULL_DOOR_AT.x, 0, CULL_DOOR_AT.z);
    const hinge = this.#doorLeaf.getObjectByName('door-hinge');
    if (!hinge) throw new Error('cull director: the personnel door has no rigged leaf');
    this.#doorHinge = hinge;
    scene.add(this.#doorLeaf);
    const dressed = scene.getObjectByName('cull-door');
    if (dressed) dressed.visible = false;
  }

  /**
   * True while the beat owns the camera — from the moment she is in the bin.
   *
   * **`#inside`, and the reason is a picture.** She walks into a
   * box, and a third-person rig behind a pig who is inside a box has nowhere to
   * stand: the arm has no collider to push it out (the bin has never had one) so
   * the lens simply ends up inside the shell, and the frame is a dark wall with a
   * verb prompt on it. `10-she-is-in.png` was that, once.
   *
   * There is nothing to solve there — any camera behind her while she is in a
   * bin is in the bin — so the beat takes the lens instead, on the same frame,
   * and cuts to the shot it was always going to end on. She is committed by then
   * anyway: `get-in` completes on `inTheBin` and `carried` begins the same tick.
   */
  get cameraHeld(): boolean {
    return this.#inside;
  }

  get fade(): number {
    return this.#fadeOverlay.value;
  }

  /**
   * True once the bin is through the doorway.
   *
   * A getter rather than a cue, because it is a fact about the world that the
   * beat's last waiting stage is hung on, and D-090 is the standing rule: the
   * world raises a flag when it has finished doing the thing, and the stage
   * waits for the flag. `main.ts` reads this and adds `CULL_FLAGS.throughTheDoor`.
   */
  get throughTheDoor(): boolean {
    return this.#ride === 'gone';
  }

  get ended(): boolean {
    return this.#ended;
  }

  /**
   * True once her body is the bin's rather than the controller's — from the
   * moment she is IN it, and it used to be from the righting.
   *
   * ── The eleven seconds she spent in the wrong room ──────────────────────────
   *
   * `enterBin()` does not move her; it only records that she is in. That is right
   * when she walked in, because she is already standing there — and **wrong on
   * both of the other two paths into this stage.** `putHerIn` (the backstop) and
   * `skipToStage('carried')` (every spec and every debug session) mark her inside
   * without her body having gone anywhere.
   *
   * Measured, on the shipped restage: with the backstop firing, she sat at
   * `(0, 0, −12)` — **the spawn, twenty-two metres back down the corridor** — for
   * the eleven seconds the man takes to walk in, while the camera watched an empty
   * bin and the story held `inTheBin`. A player who had walked half way would have
   * been standing in the middle of the frame.
   *
   * So the pin is on `#inside`, which is the same instant on all three paths.
   * Nothing is lost from the walked-in case: `carryPose` at `tip = 1` is the
   * lying pose, which is within a stride of where she walked to.
   *
   * **The `righting` still means something and it is not this.** It is when the
   * lid shuts and when the bin comes up — the world taking her, rather than the
   * simulation stopping driving her.
   */
  get carrying(): boolean {
    return this.#inside;
  }

  /**
   * Where her body must be put while she is being carried.
   *
   * Interpolated across the righting by `#tip`, so she comes up with the bin
   * rather than snapping from its side to its base on one frame. See `BIN_POSE`
   * for why the two ends are authored separately.
   */
  get carryPose(): { x: number; y: number; z: number; facing: number } {
    const t = Math.max(0, Math.min(1, this.#tip));
    return {
      x: CULL_BIN_AT.x,
      y: BIN_POSE.upright.y + (BIN_POSE.lying.y - BIN_POSE.upright.y) * t,
      z: this.#binZ + (BIN_POSE.upright.dz + (BIN_POSE.lying.dz - BIN_POSE.upright.dz) * t),
      facing: 0,
    };
  }

  /**
   * True once the bin is all the way over and she could walk into it.
   *
   * The threshold is 0.98 rather than 1 for the reason every eased flag in this
   * project carries: the beat may not offer a hole that is still closing.
   */
  get binDown(): boolean {
    return this.#tip >= 0.98;
  }

  /** True once she is in the bin, whether or not anybody has picked it up yet. */
  get inside(): boolean {
    return this.#inside;
  }

  /**
   * Where the bin is, as one number.
   *
   * A getter rather than reaching through `debug`, which builds a fresh object
   * on every read and is called from the fixed step by the containment test.
   */
  get binZ(): number {
    return this.#binZ;
  }

  /** Seconds into the plant's cycle, for a spec that wants to prove it is a clock. */
  get cyclePhase(): number {
    return this.#cycle;
  }

  /** What the beat's own gate reads instead of counting pixels. */
  get debug(): {
    cycle: number; lift: number; binZ: number; collectorZ: number;
    carrying: boolean; ride: Ride; tip: number; inside: boolean;
  } {
    return {
      cycle: this.#cycle,
      lift: this.#builtLift,
      binZ: this.#binZ,
      collectorZ: this.#collectorZ,
      carrying: this.carrying,
      ride: this.#ride,
      tip: this.#tip,
      inside: this.#inside,
    };
  }

  handle(cue: Cue): void {
    switch (cue.id) {
      case CULL_CUES.open:
        // `FadeOverlay` exists at full black from the frame it is constructed —
        // see its comment — so the room's first frame is the one this cue asks
        // for and not whatever the loader happened to have ready.
        this.#fadeOverlay.setTarget(0);
        break;

      case CULL_CUES.firstCycle:
        // The clock starts here and not at scene assembly, so that the loop the
        // engine is about to play and the timer the sheeting reads are the same
        // age. See the header.
        this.#running = true;
        this.#cycle = 0;
        // **And the first thump is struck here, not waited for.**
        //
        // `step()` only fires `onPlantCycle` when the cycle WRAPS, so starting
        // the clock at zero meant the first thump landed 4.2 s later — while the
        // hiss, which fires on crossing 0.35 s, landed first. The room's opening
        // statement played back to front: air, then the machine that moved it.
        // Stage 2 exists for no other reason than to establish this rhythm
        // before she is close enough to look at the sheeting, so this is the one
        // cycle in the beat where the order actually carries the meaning.
        // Independent review, session 24.
        this.#options.onPlantCycle({ x: CULL.maxX + 1.5, y: 1.4, z: 0 });
        break;

      case CULL_CUES.theBay:
        this.#liftScale = LIFT.near;
        break;

      case CULL_CUES.theDoor:
        this.#liftScale = LIFT.far;
        break;

      case CULL_CUES.tipItOver:
        // The FIRST anti-softlock, and it is the gentler of the two: a player who
        // never found `nudge` gets the bin knocked over for them, in frame, with
        // no fade. Nothing has happened to her — the opening is simply there now,
        // and getting into it is still hers to do. See `TIP_PATIENCE`.
        this.tipOver();
        break;

      case CULL_CUES.putHerIn:
        // The second, and the hard one. She is put in the bin and the ride is the
        // same ride — there is no lesser version of the shot for a player who did
        // not work it out. See `IN_PATIENCE` in the beat.
        //
        // A cut to black and a fade back in, not a fade out and a fade in: this
        // cue is emitted on the stage's *exit* and `carried` on the next stage's
        // *enter*, which are the same tick. Two targets set a frame apart would
        // resolve to no black at all, so the only honest way to spend a fade
        // here is the way that does not need two frames to happen in.
        //
        // The bin is tipped instantly rather than dropped: this happens behind
        // black, and a topple nobody can see is a topple that only has to be
        // true by the time the picture comes back.
        this.#tip = 1;
        this.#tipDirection = 0;
        this.#fadeOverlay.snapToBlackAndFadeIn();
        this.enterBin();
        break;

      case CULL_CUES.carried:
        // Belt and braces, and the braces are the ones that matter: the ride
        // stage assumes the bin is down and she is in it, and the ways of getting
        // there are two completed stages or the two backstop cues. A
        // `skipToStage('carried')` — which is how every spec and every debug
        // session reaches this shot — runs none of them.
        this.#tip = 1;
        this.#tipDirection = 0;
        this.enterBin();
        this.#ride = 'arriving';
        this.#rideT = 0;
        this.#collector.visible = true;
        break;

      case CULL_CUES.fadeOut:
        this.#fadeOverlay.setTarget(1);
        break;

      case CULL_CUES.end:
        // **And the machine stops being simulated.**
        //
        // `#ended` was written here and read by nobody: the `ended` getter had
        // no callers, `step()` gated only on `#running`, and `#running` was set
        // at `firstCycle` and never cleared. `disposeBeat` is wired to WebGL
        // context loss and nothing else, and `BEATS[5]` has no `next` — so beat
        // 5 ends on black and stays there with `step()` still being called every
        // fixed tick. The thump and the rustle went on playing behind the black
        // screen, indefinitely, and that was the last thing a player of Act 1
        // heard.
        //
        // This is D-087 and D-090's shape for the third time — a thing the world
        // does on its own schedule, wired to a flag that only described a
        // moment. Found by independent review, session 24; every gate this beat
        // has was green with it in place, because no spec listens after `end`.
        this.#ended = true;
        this.#running = false;
        break;

      default:
        break;
    }
  }

  /**
   * Shove it over. The verb's whole effect, and it does not touch her at all.
   *
   * Called by `main.ts` when the `nudge` hold completes, and by `tipItOver` when
   * the player never found it. **Idempotent**, because the interactable is
   * disabled on completion but the backstop cue can still arrive on the same
   * tick, and a bin that started falling twice would fall at double speed.
   */
  tipOver(): void {
    if (this.#tipDirection !== 0 || this.#tip > 0) return;
    this.#tipDirection = 1;
    this.#options.onBinFall({ x: CULL_BIN_AT.x, y: 0.55, z: this.#binZ });
  }

  /**
   * She is in the bin — on her own feet, and still hers to drive.
   *
   * **This no longer takes her body**, and that is the whole of Alex's note about
   * the beat. Her body is pinned to the bin from this instant — see `carrying`
   * for the eleven seconds that cost — but nothing has *taken* her yet: the lid
   * is still open, the bin is on the floor, and it is a man standing it back up
   * that closes both. The old version raised the carry flag on the `nudge`
   * itself, which is why nudging the bin *put her into* it.
   *
   * **It raises no story flag itself** — `main.ts` raises `CULL_FLAGS.inTheBin`
   * from the fixed step once her position has actually been tested against
   * `cullBinInterior()`, which is D-090's rule: a flag describes what the world
   * has finished doing, never what the player has just asked for.
   */
  enterBin(): void {
    if (this.#inside) return;
    this.#inside = true;
  }

  /** The fixed step. Everything the rest of the simulation can observe moves here. */
  step(dt: number): void {
    if (this.#running) {
      const before = this.#cycle;
      this.#cycle += dt;
      if (this.#cycle >= PLANT.period) {
        this.#cycle -= PLANT.period;
        this.#options.onPlantCycle({ x: CULL.maxX + 1.5, y: 1.4, z: 0 });
      }
      // The sheeting's own sound fires on the step the hiss crosses, not every
      // step it is lifted: a rustle per frame is a texture, and this one is
      // supposed to be an event that happens to a piece of plastic.
      const crossed = before < PLANT.hiss && this.#cycle >= PLANT.hiss;
      const wrapped = this.#cycle < before;
      if (crossed || (wrapped && this.#cycle >= PLANT.hiss)) {
        this.#options.onSheeting({ x: CULL.maxX, y: 1.3, z: 0 });
      }
    }

    this.#stepTip(dt);
    this.#stepRide(dt);
    this.#stepDoor(dt);
  }

  /**
   * The topple, and the righting, which are the same number driven two ways.
   *
   * Fixed step and not `update()`, because `binDown` gates a story stage and
   * `carryPose` is read by the character controller — both are simulation, and a
   * simulation that branches on a display-rate value is D-045's prohibition.
   */
  #stepTip(dt: number): void {
    if (this.#tipDirection === 0) return;
    const seconds = this.#tipDirection > 0 ? TIP.fallSeconds : TIP.rightSeconds;
    this.#tip += (this.#tipDirection * dt) / seconds;
    if (this.#tip >= 1) {
      this.#tip = 1;
      this.#tipDirection = 0;
    } else if (this.#tip <= 0) {
      this.#tip = 0;
      this.#tipDirection = 0;
    }
  }

  /** The render step. Meshes catch up with decisions already made in `step`. */
  update(frameDt: number): void {
    const dt = Number.isFinite(frameDt) ? Math.max(0, Math.min(0.1, frameDt)) : 0;

    // The lift is a fast rise on the hiss and a slower fall back, which is what
    // air does to a hanging sheet. Before the hiss it is flat: the thump alone
    // moves nothing, and a player watching the plastic learns the difference
    // between the machine's two sounds without being told there is one.
    const phase = this.#cycle;
    let shape = 0;
    if (phase >= PLANT.hiss && phase < PLANT.settle) {
      const t = (phase - PLANT.hiss) / (PLANT.settle - PLANT.hiss);
      // Rise over the first quarter, fall over the rest.
      shape = t < 0.25 ? t / 0.25 : 1 - (t - 0.25) / 0.75;
    }
    this.#setLift(this.#running ? shape * this.#liftScale : 0);

    // `parts.bin` is the PIVOT group, not the bin mesh — see `dressCullCorridor`.
    // Its origin is the bin's downstream bottom edge, so its z carries the offset
    // and its x rotation is the topple.
    this.#parts.bin.position.z = this.#binZ + CULL_BIN.tipPivotDz;
    this.#parts.bin.rotation.x = this.#tip * (Math.PI / 2);
    this.#collector.position.z = this.#collectorZ;
    this.#collector.rotation.y = Math.PI;
    this.#animateCollector(dt);
    this.#doorHinge.rotation.y = this.#doorAngle;

    this.#fadeOverlay.update(dt);
  }

  /**
   * The shot, once the man has taken hold of the bin. Null the rest of the time.
   *
   * **Not while she is merely inside it.** She walks in on her own feet and the
   * rig keeps the camera for that, because getting into the bin is the last thing
   * in the beat she does and a cinematic framing over it would take it away from
   * her. The beat takes the lens at the instant her body stops being hers, which
   * is `righting` — see `#stepRide`.
   *
   * Across the corridor and trailing: the bin, the man behind it, and the room
   * going past both of them. `RIDE_CAMERA`'s comment has the argument for why
   * this is outside the bin and not in it.
   */
  framing(): Framing | null {
    if (!this.#inside) return null;
    // Aimed at the middle of the bin whichever way up it is. `#binZ` is its base
    // centre, which while it is lying down is its FOOT — a look point there puts
    // the bin in the bottom of the frame and the man behind it out of the top.
    const middle = this.#binZ + this.#tip * (CULL_BIN.tipPivotDz + CULL_BIN.tipLength / 2);
    return {
      position: { x: RIDE_CAMERA.x, y: RIDE_CAMERA.y, z: this.#binZ - RIDE_CAMERA.trail },
      // The look point travels with the bin, so the ride is one continuous move
      // rather than a camera that swings to keep something in frame.
      lookAt: { x: RIDE_CAMERA.lookX, y: RIDE_CAMERA.lookY, z: middle },
      fov: RIDE_CAMERA.fov,
    };
  }

  dispose(): void {
    this.#fadeOverlay.dispose();
    this.#collector.removeFromParent();
    this.#doorLeaf.removeFromParent();
    disposeAsset(this.#collector);
    disposeAsset(this.#doorLeaf);
  }

  #stepRide(dt: number): void {
    if (this.#ride === 'idle' || this.#ride === 'gone') {
      this.#options.onCastors(0, { x: CULL_BIN_AT.x, y: 0.2, z: this.#binZ });
      return;
    }
    this.#rideT += dt;

    if (this.#ride === 'arriving') {
      this.#doorTarget = 1;
      // He comes through the door and walks PAST the bin, to the upstream side —
      // the side a man pushes a bin from. He used to stop short of it and then
      // teleport across it on the frame the push began; see `COLLECTOR.standOff`.
      const target = this.#binZ - COLLECTOR.standOff;
      const step = COLLECTOR.walkSpeed * dt;
      const remaining = this.#collectorZ - target;
      if (remaining > step) {
        this.#collectorZ -= step;
        this.#accumulateGait(step);
      } else {
        this.#collectorZ = target;
        this.#ride = 'righting';
        this.#rideT = 0;
        // He stands it back up, with her in it, and the beat's worst sentence is
        // that this costs him nothing: it is a bin, it was lying down, and it is
        // on his round. Her body was already the bin's (see `carrying`); what
        // happens here is that the bin — and she with it — comes off the floor.
        this.#tipDirection = -1;
        // ── The lid, and it belongs HERE ────────────────────────────────────
        //
        // It used to fire when she got in, which was true of the old beat — she
        // was teleported into an upright bin and the lid came down on her. In
        // this one she walks into a bin lying open on the floor, and a
        // lid-closing sound at that moment has nothing to close. What actually
        // shuts it is the man standing it back up: the lid falls the moment the
        // bin comes vertical, which is the same event as her losing the light.
        //
        // So the line the beat is built on — *the last thing she hears from
        // outside* — is now attached to the thing that makes it true.
        if (!this.#lidPlayed) {
          this.#lidPlayed = true;
          this.#options.onLid({ x: CULL_BIN_AT.x, y: 1.0, z: this.#binZ });
        }
      }
      this.#options.onCastors(0, { x: CULL_BIN_AT.x, y: 0.2, z: this.#binZ });
      return;
    }

    if (this.#ride === 'righting') {
      // The bin comes up on `#stepTip`'s clock; this stage waits for it and then
      // for the rest of the dwell. A stage that held its own clock over work
      // somebody else is doing is D-090, so the condition is the tip's own value
      // and the dwell is only the pause AFTER it is up.
      if (this.#tip <= 0 && this.#rideT >= TIP.rightSeconds + COLLECTOR.dwell) {
        this.#ride = 'pushing';
        this.#rideT = 0;
      }
      this.#options.onCastors(0, { x: CULL_BIN_AT.x, y: 0.2, z: this.#binZ });
      return;
    }

    // Pushing. The bin accelerates over the first metre and a half rather than
    // starting at speed, because a loaded bin does, and because the castor loop
    // is played at a rate taken from this number.
    this.#binSpeed = Math.min(COLLECTOR.pushSpeed, this.#binSpeed + dt * 0.55);
    const travelled = this.#binSpeed * dt;
    this.#binZ += travelled;
    this.#collectorZ = this.#binZ - COLLECTOR.standOff;
    this.#accumulateGait(travelled);
    this.#options.onCastors(this.#binSpeed, { x: CULL_BIN_AT.x, y: 0.2, z: this.#binZ });

    // Through the doorway and into the threshold. `throughTheDoor` goes true on
    // the world having done it, not on a clock (D-090), and the beat's own
    // backstop covers the case where it somehow never arrives.
    if (this.#binZ >= CULL_THRESHOLD.maxZ - 1.6) this.#ride = 'gone';
  }

  #stepDoor(dt: number): void {
    const rate = 1.6 * dt;
    const delta = this.#doorTarget - this.#doorAngle;
    if (Math.abs(delta) <= rate) this.#doorAngle = this.#doorTarget;
    else this.#doorAngle += Math.sign(delta) * rate;
  }

  #accumulateGait(distance: number): void {
    const before = this.#gaitDistance;
    this.#gaitDistance += distance;
    if (Math.floor(this.#gaitDistance / COLLECTOR.stride) !== Math.floor(before / COLLECTOR.stride)) {
      this.#options.onFootfall({ x: 0, y: 0, z: this.#collectorZ });
    }
  }

  /**
   * His gait, off distance travelled rather than off a clock.
   *
   * D-092's rule, and the third director to follow it: the leg that swings
   * forward is the one the footfall above just played for. A whole figure that
   * slides is worse than no figure at all.
   */
  #animateCollector(frameDt: number): void {
    const moving = this.#ride === 'arriving' || this.#ride === 'pushing';
    const swing = moving ? Math.sin((this.#gaitDistance / COLLECTOR.stride) * Math.PI) : 0;
    const settle = 1 - Math.exp(-8 * frameDt);
    const pose = (node: THREE.Object3D, target: number): void => {
      node.rotation.x += (target - node.rotation.x) * settle;
    };
    pose(this.#collectorParts.hips[0], swing * 0.42);
    pose(this.#collectorParts.hips[1], -swing * 0.42);
    // His arms are on the bin once he is pushing, so they stop swinging and go
    // forward and stay there. It is two numbers and it is the difference between
    // a man pushing a bin and a man walking behind one.
    const pushing = this.#ride === 'pushing';
    pose(this.#collectorParts.shoulders[0], pushing ? -0.85 : -swing * 0.26);
    pose(this.#collectorParts.shoulders[1], pushing ? -0.85 : swing * 0.26);
  }

  /**
   * Rebuild the panels, quantised.
   *
   * Rebuilt rather than animated for the reason `bornDirector.ts` gives about
   * the strip curtain: the factory is pure, the panels cost nothing, and
   * reaching into an asset's construction from a director is exactly what the
   * asset seam exists to prevent. Quantised because rebuilding five geometries
   * for a change of a thousandth is work nobody can see.
   */
  #setLift(lift: number): void {
    const quantised = Math.round(Math.max(0, Math.min(1, lift)) * 12) / 12;
    if (quantised === this.#builtLift) return;
    this.#builtLift = quantised;

    const parent = this.#parts.sheeting.parent;
    const old = this.#parts.sheeting;
    old.removeFromParent();
    // Disposed, not merely detached: WebGL resources are not garbage collected,
    // and on this box a leaked pile of them presents as a lost context (D-019),
    // which is to say as somebody else's problem.
    disposeAsset(old);
    const next = instanceGroup(plasticSheeting(quantised), [{ x: 0, z: 0 }], 'cull-sheeting');
    next.position.set(SHEETING_POSE.x, SHEETING_POSE.y, SHEETING_POSE.z);
    next.rotation.y = SHEETING_POSE.rotY;
    this.#parts.sheeting = next;
    parent?.add(next);
  }
}
